import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Users, FileText, DollarSign, Package, Bell, ChevronRight,
  AlertTriangle, CheckCircle, Smile, CloudRain, Clock, Heart,
  ArrowUpRight, Calendar
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOOD_MAP = {
  happy: { label: 'Happy', color: '#f59e0b', icon: '😊' },
  calm: { label: 'Calm', color: '#3b82f6', icon: '😌' },
  tired: { label: 'Tired', color: '#8b5cf6', icon: '😴' },
  upset: { label: 'Upset', color: '#ef4444', icon: '😢' },
};

const REPORT_BADGE = {
  incident: { label: 'New Incident', bg: '#fee2e2', color: '#dc2626' },
  daily: { label: 'Good Day', bg: '#dcfce7', color: '#16a34a' },
};

const AVATAR_COLORS = ['#6366f1', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ef4444'];

function getInitials(first, last) {
  return `${(first || '')[0] || ''}${(last || '')[0] || ''}`.toUpperCase();
}

function getAvatarColor(name) {
  let hash = 0;
  for (let i = 0; i < (name || '').length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs > 1 ? 's' : ''} ago`;
  return `${Math.floor(hrs / 24)} day${Math.floor(hrs / 24) > 1 ? 's' : ''} ago`;
}

export default function DaycareDashboard({ tenantId, onNavigate, brandName }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upcomingAppts, setUpcomingAppts] = useState([]);

  useEffect(() => {
    if (!tenantId) return;
    Promise.all([
      axios.get(`${API}/daycare/${tenantId}/dashboard`),
      axios.get(`${API}/appointments/${tenantId}/upcoming?days=7`).catch(() => ({ data: [] })),
    ]).then(([dashRes, apptRes]) => {
      setStats(dashRes.data);
      setUpcomingAppts(apptRes.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [tenantId]);

  if (loading || !stats) {
    return <div className="pdb-loading">Loading dashboard...</div>;
  }

  const chartData = [
    { month: 'Jan', Income: 8500, Expenses: 5200 },
    { month: 'Feb', Income: 9200, Expenses: 5800 },
    { month: 'Mar', Income: stats.monthly_income || 7800, Expenses: 4900 },
    { month: 'Apr', Income: (stats.monthly_income || 0) + (stats.outstanding_tuition || 0), Expenses: 6100 },
  ];

  const nav = (page) => { if (onNavigate) onNavigate(page); };

  return (
    <div className="pdb" data-testid="premium-daycare-dashboard">
      {/* KPI Cards */}
      <div className="pdb-kpi-row">
        <div className="pdb-kpi" onClick={() => nav('children')} data-testid="kpi-children">
          <div className="pdb-kpi-left">
            <span className="pdb-kpi-title">Active Children</span>
            <span className="pdb-kpi-value">{stats.active_children}</span>
            <span className="pdb-kpi-sub positive"><ArrowUpRight size={14} /> +{stats.new_children_week} this week</span>
          </div>
          <div className="pdb-kpi-icon green">
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none"><circle cx="20" cy="20" r="10" fill="#86efac"/><circle cx="38" cy="18" r="8" fill="#4ade80"/><circle cx="28" cy="36" r="12" fill="#22c55e"/><circle cx="20" cy="20" r="5" fill="#fff" opacity="0.5"/><circle cx="38" cy="18" r="4" fill="#fff" opacity="0.5"/><circle cx="28" cy="36" r="6" fill="#fff" opacity="0.4"/><path d="M16 28c2-4 8-6 12-2s6 8 2 12" stroke="#16a34a" strokeWidth="1.5" fill="none"/></svg>
          </div>
        </div>

        <div className="pdb-kpi" onClick={() => nav('reports')} data-testid="kpi-reports">
          <div className="pdb-kpi-left">
            <span className="pdb-kpi-title">Reports Today</span>
            <span className="pdb-kpi-value">{stats.reports_today}</span>
            <span className="pdb-kpi-sub warn"><AlertTriangle size={14} /> {stats.pending_reports} pending review</span>
          </div>
          <div className="pdb-kpi-icon blue">
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none"><rect x="12" y="6" width="36" height="48" rx="4" fill="#93c5fd"/><rect x="18" y="16" width="20" height="3" rx="1.5" fill="#fff"/><rect x="18" y="24" width="16" height="3" rx="1.5" fill="#fff"/><rect x="18" y="32" width="22" height="3" rx="1.5" fill="#fff"/><rect x="18" y="40" width="14" height="3" rx="1.5" fill="#fff"/><circle cx="44" cy="48" r="12" fill="#3b82f6"/><path d="M40 48l3 3 5-6" stroke="#fff" strokeWidth="2" strokeLinecap="round"/></svg>
          </div>
        </div>

        <div className="pdb-kpi" onClick={() => nav('payments')} data-testid="kpi-tuition">
          <div className="pdb-kpi-left">
            <span className="pdb-kpi-title">Outstanding Tuition</span>
            <span className="pdb-kpi-value">${stats.outstanding_tuition?.toLocaleString('en-US', { minimumFractionDigits: 0 })}</span>
            <span className="pdb-kpi-sub danger"><AlertTriangle size={14} /> {stats.overdue_count} overdue accounts</span>
          </div>
          <div className="pdb-kpi-icon red">
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none"><rect x="8" y="14" width="40" height="36" rx="4" fill="#fca5a5"/><rect x="14" y="22" width="28" height="6" rx="2" fill="#fff" opacity="0.6"/><circle cx="28" cy="38" r="8" fill="#ef4444"/><text x="28" y="42" textAnchor="middle" fill="#fff" fontSize="12" fontWeight="bold">$</text><rect x="36" y="8" width="20" height="28" rx="3" fill="#f87171"/><path d="M42 16v8M46 20h-8" stroke="#fff" strokeWidth="2" strokeLinecap="round"/></svg>
          </div>
        </div>

        <div className="pdb-kpi" onClick={() => nav('supplies')} data-testid="kpi-supplies">
          <div className="pdb-kpi-left">
            <span className="pdb-kpi-title">Low Supplies</span>
            <span className="pdb-kpi-value">{stats.low_supplies}</span>
            <span className="pdb-kpi-sub warn"><CheckCircle size={14} /> Needs reorder</span>
          </div>
          <div className="pdb-kpi-icon yellow">
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none"><rect x="8" y="28" width="24" height="20" rx="3" fill="#fcd34d"/><rect x="12" y="24" width="16" height="6" rx="2" fill="#f59e0b"/><line x1="20" y1="24" x2="20" y2="48" stroke="#fff" strokeWidth="2"/><rect x="28" y="16" width="20" height="16" rx="3" fill="#fbbf24"/><rect x="32" y="12" width="12" height="6" rx="2" fill="#f59e0b"/><rect x="36" y="36" width="18" height="22" rx="3" fill="#fde68a"/><rect x="40" y="32" width="10" height="6" rx="2" fill="#fbbf24"/></svg>
          </div>
        </div>
      </div>

      {/* Main grid: Daily Reports + Notifications */}
      <div className="pdb-main-grid">
        <div className="pdb-section">
          <div className="pdb-section-header">
            <h3>Daily Report Overview</h3>
            <button className="pdb-view-all" onClick={() => nav('reports')} data-testid="view-all-reports">View All Reports <ChevronRight size={16} /></button>
          </div>
          <div className="pdb-reports-grid">
            {(stats.latest_reports || []).slice(0, 3).map((r, idx) => {
              const badge = REPORT_BADGE[r.type] || REPORT_BADGE.daily;
              const mood = MOOD_MAP[r.mood] || MOOD_MAP.happy;
              const initials = getInitials(r.child_first_name || r.child_name?.split(' ')[0], r.child_last_name || r.child_name?.split(' ')[1]);
              const name = r.child_first_name ? `${r.child_first_name} ${r.child_last_name || ''}` : r.child_name || 'Child';
              const avatarBg = getAvatarColor(name);
              const photoUrl = r.photo_url
                ? `${process.env.REACT_APP_BACKEND_URL}${r.photo_url}`
                : (r.child_photo_url ? `${process.env.REACT_APP_BACKEND_URL}${r.child_photo_url}` : '');
              const notes = [r.activities, r.meals, r.nap_time].filter(Boolean);

              return (
                <div key={r.id || idx} className="pdb-report-card" onClick={() => nav('reports')} data-testid={`report-card-${idx}`}>
                  <div className="pdb-report-img">
                    {photoUrl ? (
                      <img src={photoUrl} alt={name} className="pdb-report-photo" />
                    ) : (
                      <div className="pdb-report-fallback" style={{ background: `linear-gradient(135deg, ${avatarBg}60, ${avatarBg}30)` }}>
                        <div className="pdb-report-avatar" style={{ background: avatarBg }}>{initials}</div>
                      </div>
                    )}
                    <span className="pdb-report-badge" style={{ background: badge.bg, color: badge.color }}>{badge.label}</span>
                  </div>
                  <div className="pdb-report-body">
                    <div className="pdb-report-name">
                      <span className="pdb-name-dot" style={{ background: mood.color }} />
                      {name}
                    </div>
                    <div className="pdb-report-mood" style={{ color: mood.color }}>
                      {mood.icon} <strong>{mood.label}</strong>
                    </div>
                    <div className="pdb-report-notes">
                      {notes.slice(0, 3).map((n, i) => (
                        <span key={i}>{n.length > 40 ? n.slice(0, 40) + '...' : n}</span>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
            {(!stats.latest_reports || stats.latest_reports.length === 0) && (
              <div className="pdb-empty-card">No reports yet today</div>
            )}
          </div>
        </div>

        {/* Notifications */}
        <div className="pdb-notif-panel">
          <h3>Recent Notifications</h3>
          <div className="pdb-notif-list">
            {(stats.recent_notifications || []).slice(0, 6).map((n, idx) => {
              const iconMap = {
                overdue_payment: { icon: AlertTriangle, bg: '#fee2e2', color: '#dc2626' },
                low_supply: { icon: Package, bg: '#dbeafe', color: '#2563eb' },
                report_sent: { icon: FileText, bg: '#fef3c7', color: '#d97706' },
                parent_invite: { icon: Users, bg: '#dcfce7', color: '#16a34a' },
                welcome_email: { icon: Heart, bg: '#fce7f3', color: '#db2777' },
              };
              const cfg = iconMap[n.trigger] || { icon: Bell, bg: '#f1f5f9', color: '#64748b' };
              const Icon = cfg.icon;
              return (
                <div key={n.id || idx} className="pdb-notif-item" onClick={() => nav('notifications')} data-testid={`notif-${idx}`}>
                  <div className="pdb-notif-icon" style={{ background: cfg.bg, color: cfg.color }}><Icon size={18} /></div>
                  <div className="pdb-notif-content">
                    <strong>{n.message?.length > 40 ? n.message.slice(0, 40) + '...' : n.message}</strong>
                    <span>{timeAgo(n.created_at)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Bottom grid: Chart + Upcoming Payments */}
      <div className="pdb-bottom-grid">
        <div className="pdb-chart-card">
          <div className="pdb-section-header">
            <h3>Monthly Income & Expenses</h3>
            <div className="pdb-chart-legend">
              <span className="pdb-legend-dot" style={{ background: '#22c55e' }} /> Income
              <span className="pdb-legend-dot" style={{ background: '#f59e0b' }} /> Expenses
            </div>
          </div>
          <div className="pdb-chart-wrap">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData} barCategoryGap="25%">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 13 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#64748b', fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={v => `$${(v / 1000).toFixed(0)}K`} />
                <Tooltip formatter={(v) => `$${v.toLocaleString()}`} contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0' }} />
                <Bar dataKey="Income" fill="#22c55e" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Expenses" fill="#f59e0b" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="pdb-payments-card">
          <div className="pdb-section-header">
            <h3>Upcoming Payments</h3>
          </div>
          <div className="pdb-payments-list">
            {(stats.upcoming_payments || []).slice(0, 5).map((p, idx) => {
              const name = p.parent_name || 'Parent';
              const statusColors = { overdue: '#dc2626', pending: '#f59e0b', paid: '#16a34a' };
              return (
                <div key={p.id || idx} className="pdb-payment-row" onClick={() => nav('payments')} data-testid={`payment-${idx}`}>
                  <div className="pdb-payment-avatar" style={{ background: getAvatarColor(name) }}>
                    {getInitials(name.split(' ')[0], name.split(' ')[1])}
                  </div>
                  <div className="pdb-payment-info">
                    <strong>{name}</strong>
                    <span>Due {p.due_date || '—'}</span>
                  </div>
                  <div className="pdb-payment-amount">
                    <strong>${Number(p.amount).toLocaleString('en-US', { minimumFractionDigits: 0 })}</strong>
                    <span style={{ color: statusColors[p.status] || '#64748b' }}>{p.status ? p.status.charAt(0).toUpperCase() + p.status.slice(1) : ''}</span>
                  </div>
                </div>
              );
            })}
            {(!stats.upcoming_payments || stats.upcoming_payments.length === 0) && (
              <div className="pdb-empty-card">No upcoming payments</div>
            )}
          </div>
        </div>
      </div>

      {/* Upcoming Appointments */}
      <div className="pdb-section" style={{ marginTop: 20 }}>
        <div className="pdb-section-header">
          <h3>Upcoming Appointments</h3>
          <button className="pdb-view-all" onClick={() => nav('calendar')} data-testid="view-all-appointments">View Calendar <ChevronRight size={16} /></button>
        </div>
        {upcomingAppts.length > 0 ? (
          <div className="pdb-payments-list">
            {upcomingAppts.slice(0, 5).map((a, idx) => (
              <div key={a.id || idx} className="pdb-payment-row" onClick={() => nav('calendar')} data-testid={`appt-${idx}`}>
                <div className="pdb-payment-avatar" style={{ background: '#3b82f6' }}>
                  <Calendar size={18} color="#fff" />
                </div>
                <div className="pdb-payment-info">
                  <strong>{a.client_name || 'Appointment'}</strong>
                  <span>{a.date} {a.start_time}–{a.end_time || ''} &middot; {a.service_type || ''}</span>
                </div>
                <div className="pdb-payment-amount">
                  <span style={{ color: '#3b82f6', fontWeight: 600, textTransform: 'capitalize' }}>{a.status}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="pdb-empty-card">No upcoming appointments</div>
        )}
      </div>
    </div>
  );
}
